import axios from 'axios'
import {
  Button,
  FormControlLabel,
  FormHelperText,
  FormGroup,
  IconButton,
  InputAdornment,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core'
import { AddOutlined, Visibility, VisibilityOff } from '@material-ui/icons'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { authConfigHost, authSecurityHost } from 'utils/Hosts'

import { ClientAddress, ClientLabelEditor } from './ClientLabelEditor'
import { SecondarySettingsContent, SettingSectionLabel } from './style'

const emptyConfig = {
  enabled: false,
  effective_enabled: false,
  legacy_mode: false,
  username: 'lampa',
  password_configured: false,
  readable_password: '',
  store_plaintext_credentials: false,
  plaintext_credentials_configured: false,
  attempt_interval_seconds: 60,
  daily_lockdown_enabled: false,
  daily_failure_limit: 100,
  whitelist: [],
  whitelist_bypass_auth: false,
  client_labels: {},
  config_file: 'auth.json',
  password_file: 'lampa-password.txt',
  data_directory: '',
  config_path: '',
  password_path: '',
  password_file_exists: false,
}

const sectionStyle = {
  padding: '18px',
  marginBottom: '16px',
  borderRadius: '12px',
  borderColor: 'rgba(127, 127, 127, 0.28)',
  background: 'rgba(127, 127, 127, 0.035)',
}

const sectionTitleStyle = {
  fontWeight: 600,
  marginBottom: '8px',
}

const addKnownAddress = (addresses, rawAddress) => {
  const address = String(rawAddress || '').trim()
  if (address && !/[\s,%]/.test(address) && /^[0-9a-f:.]+$/i.test(address)) {
    addresses.add(address)
  }
}

const addReportedAddresses = (addresses, value) => {
  String(value || '')
    .split(',')
    .forEach(address => addKnownAddress(addresses, address))
}

const collectKnownAddresses = (labels, snapshot) => {
  const addresses = new Set(Object.keys(labels || {}))
  ;(snapshot?.sessions || []).forEach(session => {
    addKnownAddress(addresses, session.source)
    addReportedAddresses(addresses, session.forwarded_for)
  })
  ;(snapshot?.events || []).forEach(event => {
    addKnownAddress(addresses, event.source)
    addReportedAddresses(addresses, event.forwarded_for)
  })
  addKnownAddress(addresses, snapshot?.blocking_source)
  addReportedAddresses(addresses, snapshot?.blocking_forwarded_for)
  return Array.from(addresses).sort((first, second) =>
    first.localeCompare(second, undefined, {
      numeric: true,
      sensitivity: 'base',
    }),
  )
}

export default function SecuritySettings() {
  const { t } = useTranslation()
  const [config, setConfig] = useState(emptyConfig)
  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [passwordVisible, setPasswordVisible] = useState(false)
  const [whitelistText, setWhitelistText] = useState('')
  const [knownAddresses, setKnownAddresses] = useState([])
  const [labelEditor, setLabelEditor] = useState({
    open: false,
    address: '',
    label: '',
    allowAddressEdit: false,
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [status, setStatus] = useState({ type: '', message: '' })

  useEffect(() => {
    Promise.all([axios.get(authConfigHost()), axios.get(authSecurityHost()).catch(() => ({ data: {} }))])
      .then(([{ data }, securityResponse]) => {
        const loaded = { ...emptyConfig, ...data }
        setConfig(loaded)
        setPassword(loaded.readable_password || '')
        setPasswordConfirmation(loaded.readable_password || '')
        setWhitelistText((loaded.whitelist || []).join('\n'))
        setKnownAddresses(collectKnownAddresses(loaded.client_labels, securityResponse.data))
      })
      .catch(error => {
        setStatus({
          type: 'error',
          message: error.response?.data?.error || t('Security.LoadError'),
        })
      })
      .finally(() => setLoading(false))
  }, [t])

  const updateConfig = ({ target: { name, value, checked, type } }) => {
    setConfig(current => ({
      ...current,
      [name]: type === 'checkbox' ? checked : type === 'number' ? Number(value) : value,
    }))
  }

  const save = async () => {
    setStatus({ type: '', message: '' })
    if (password !== passwordConfirmation) {
      setStatus({ type: 'error', message: t('Security.PasswordMismatch') })
      return
    }
    if (config.enabled && !config.password_configured && !password) {
      setStatus({ type: 'error', message: t('Security.PasswordRequired') })
      return
    }
    if (config.store_plaintext_credentials && !config.plaintext_credentials_configured && !password) {
      setStatus({
        type: 'error',
        message: t('Security.PlaintextPasswordRequired'),
      })
      return
    }
    if (config.attempt_interval_seconds < 1 || config.attempt_interval_seconds > 3600) {
      setStatus({ type: 'error', message: t('Security.IntervalRange') })
      return
    }
    if (config.daily_failure_limit < 1 || config.daily_failure_limit > 1000000) {
      setStatus({ type: 'error', message: t('Security.DailyLimitRange') })
      return
    }
    const whitelist = whitelistText
      .split(/[\s,;]+/)
      .map(value => value.trim())
      .filter(Boolean)
    if ((config.daily_lockdown_enabled || config.whitelist_bypass_auth) && !whitelist.length) {
      setStatus({ type: 'error', message: t('Security.WhitelistRequired') })
      return
    }
    setSaving(true)
    try {
      const { data } = await axios.post(authConfigHost(), {
        enabled: config.enabled,
        username: config.username,
        password,
        store_plaintext_credentials: config.store_plaintext_credentials,
        attempt_interval_seconds: config.attempt_interval_seconds,
        daily_lockdown_enabled: config.daily_lockdown_enabled,
        daily_failure_limit: config.daily_failure_limit,
        whitelist,
        whitelist_bypass_auth: config.whitelist_bypass_auth,
        client_labels: config.client_labels || {},
      })
      setConfig({ ...emptyConfig, ...data })
      const savedReadablePassword = data.store_plaintext_credentials ? data.readable_password || password : ''
      setPassword(savedReadablePassword)
      setPasswordConfirmation(savedReadablePassword)
      setWhitelistText((data.whitelist || []).join('\n'))
      setKnownAddresses(current =>
        collectKnownAddresses(data.client_labels, {
          sessions: current.map(source => ({ source })),
        }),
      )
      setStatus({
        type: 'success',
        message: data.enabled ? t('Security.EnabledSaved') : t('Security.DisabledSaved'),
      })
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.response?.data?.error || t('Security.SaveError'),
      })
    } finally {
      setSaving(false)
    }
  }

  const passwordLabel =
    config.readable_password || !config.password_configured ? t('Security.Password') : t('Security.NewPassword')

  const editClientLabel = (address, label, allowAddressEdit = false) => {
    setLabelEditor({ open: true, address, label, allowAddressEdit })
  }

  const clientLabelSaved = async (labels, address) => {
    setConfig(current => ({ ...current, client_labels: labels }))
    setKnownAddresses(current => {
      const addresses = new Set([...current, ...Object.keys(labels)])
      addKnownAddress(addresses, address)
      return Array.from(addresses).sort((first, second) =>
        first.localeCompare(second, undefined, {
          numeric: true,
          sensitivity: 'base',
        }),
      )
    })
  }

  const clientAddresses = Array.from(new Set([...knownAddresses, ...Object.keys(config.client_labels || {})])).sort(
    (first, second) =>
      first.localeCompare(second, undefined, {
        numeric: true,
        sensitivity: 'base',
      }),
  )

  return (
    <SecondarySettingsContent>
      <SettingSectionLabel>{t('Security.Settings')}</SettingSectionLabel>

      <Paper variant='outlined' style={sectionStyle}>
        <Typography variant='subtitle1' style={sectionTitleStyle}>
          {t('Security.AccessCredentials')}
        </Typography>

        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={config.enabled}
                onChange={updateConfig}
                name='enabled'
                color='secondary'
                disabled={loading}
              />
            }
            label={t('Security.Enable')}
            labelPlacement='start'
          />
          <FormHelperText>{t('Security.EnableHint')}</FormHelperText>
        </FormGroup>

        {config.legacy_mode && <FormHelperText error>{t('Security.LegacyMode')}</FormHelperText>}

        <TextField
          name='username'
          value={config.username}
          onChange={updateConfig}
          margin='normal'
          label={t('Security.Username')}
          helperText={t('Security.UsernameHint')}
          variant='outlined'
          inputProps={{ maxLength: 64 }}
          disabled={loading}
          fullWidth
        />

        <TextField
          value={password}
          onChange={({ target: { value } }) => setPassword(value)}
          margin='normal'
          label={passwordLabel}
          helperText={t('Security.PasswordHint')}
          type={passwordVisible ? 'text' : 'password'}
          autoComplete='new-password'
          variant='outlined'
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton
                  aria-label={passwordVisible ? t('Security.HidePassword') : t('Security.ShowPassword')}
                  onClick={() => setPasswordVisible(current => !current)}
                  edge='end'
                >
                  {passwordVisible ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          disabled={loading}
          fullWidth
        />

        <TextField
          value={passwordConfirmation}
          onChange={({ target: { value } }) => setPasswordConfirmation(value)}
          margin='normal'
          label={t('Security.ConfirmPassword')}
          type={passwordVisible ? 'text' : 'password'}
          autoComplete='new-password'
          variant='outlined'
          disabled={loading}
          fullWidth
        />

        <FormGroup style={{ marginTop: '12px' }}>
          <FormControlLabel
            control={
              <Switch
                checked={config.store_plaintext_credentials}
                onChange={updateConfig}
                name='store_plaintext_credentials'
                color='secondary'
                disabled={loading}
              />
            }
            label={t('Security.StorePlaintextCredentials')}
            labelPlacement='start'
          />
          <FormHelperText>{t('Security.StorePlaintextCredentialsHint')}</FormHelperText>
        </FormGroup>
      </Paper>

      <Paper variant='outlined' style={sectionStyle}>
        <Typography variant='subtitle1' style={sectionTitleStyle}>
          {t('Security.AttackProtection')}
        </Typography>

        <TextField
          name='attempt_interval_seconds'
          value={config.attempt_interval_seconds}
          onChange={updateConfig}
          margin='normal'
          label={t('Security.AttemptInterval')}
          helperText={t('Security.AttemptIntervalHint')}
          type='number'
          variant='outlined'
          inputProps={{ min: 1, max: 3600, step: 1 }}
          disabled={loading}
          fullWidth
        />

        <FormGroup style={{ marginTop: '18px' }}>
          <FormControlLabel
            control={
              <Switch
                checked={config.daily_lockdown_enabled}
                onChange={updateConfig}
                name='daily_lockdown_enabled'
                color='secondary'
                disabled={loading}
              />
            }
            label={t('Security.DailyLockdown')}
            labelPlacement='start'
          />
          <FormHelperText>{t('Security.DailyLockdownHint')}</FormHelperText>
        </FormGroup>

        <TextField
          name='daily_failure_limit'
          value={config.daily_failure_limit}
          onChange={updateConfig}
          margin='normal'
          label={t('Security.DailyFailureLimit')}
          helperText={t('Security.DailyFailureLimitHint')}
          type='number'
          variant='outlined'
          inputProps={{ min: 1, max: 1000000, step: 1 }}
          disabled={loading}
          fullWidth
        />

        <TextField
          value={whitelistText}
          onChange={({ target: { value } }) => setWhitelistText(value)}
          margin='normal'
          label={t('Security.Whitelist')}
          helperText={t('Security.WhitelistHint')}
          placeholder={'192.168.68.0/24\n192.168.1.20'}
          variant='outlined'
          multiline
          rows={3}
          disabled={loading}
          fullWidth
        />

        <FormGroup style={{ marginTop: '12px' }}>
          <FormControlLabel
            control={
              <Switch
                checked={config.whitelist_bypass_auth}
                onChange={updateConfig}
                name='whitelist_bypass_auth'
                color='secondary'
                disabled={loading}
              />
            }
            label={t('Security.WhitelistBypassAuth')}
            labelPlacement='start'
          />
          <FormHelperText>{t('Security.WhitelistBypassAuthHint')}</FormHelperText>
        </FormGroup>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px',
            marginTop: '20px',
          }}
        >
          <Typography variant='subtitle2' style={{ fontWeight: 600 }}>
            {t('Security.ClientLabels')}
          </Typography>
          <Button
            size='small'
            color='secondary'
            variant='outlined'
            startIcon={<AddOutlined />}
            onClick={() => editClientLabel('', '', true)}
            disabled={loading}
          >
            {t('Security.AddClientIPAddress')}
          </Button>
        </div>
        <FormHelperText style={{ marginBottom: '7px' }}>{t('Security.ClientLabelsHint')}</FormHelperText>
        <Paper
          variant='outlined'
          style={{
            maxHeight: '230px',
            overflowY: 'auto',
            borderRadius: '10px',
            background: 'rgba(127, 127, 127, 0.025)',
          }}
        >
          {clientAddresses.length ? (
            clientAddresses.map((address, index) => (
              <div
                key={address}
                style={{
                  padding: '7px 10px',
                  borderTop: index ? '1px solid rgba(127, 127, 127, 0.16)' : 'none',
                }}
              >
                <ClientAddress address={address} label={config.client_labels?.[address]} onEdit={editClientLabel} />
              </div>
            ))
          ) : (
            <Typography variant='body2' color='textSecondary' style={{ padding: '12px' }}>
              {t('Security.NoKnownClientAddresses')}
            </Typography>
          )}
        </Paper>
      </Paper>

      <Paper variant='outlined' style={{ ...sectionStyle, padding: '14px 18px' }}>
        <Typography variant='subtitle2' style={{ fontWeight: 600, marginBottom: '6px' }}>
          {t('Security.ConnectionNotes')}
        </Typography>
        <FormHelperText>{t('Security.LampaHint')}</FormHelperText>
        <FormHelperText style={{ overflowWrap: 'anywhere' }}>
          {t('Security.ConfigFileHint', {
            path: config.config_path || config.config_file,
          })}
        </FormHelperText>
        {config.store_plaintext_credentials && config.password_file_exists ? (
          <FormHelperText error style={{ overflowWrap: 'anywhere' }}>
            {t('Security.PasswordFileHint', {
              path: config.password_path || config.password_file,
            })}
          </FormHelperText>
        ) : config.store_plaintext_credentials ? (
          <FormHelperText error style={{ overflowWrap: 'anywhere' }}>
            {t('Security.PasswordFileMissingHint', {
              path: config.password_path || config.password_file,
            })}
          </FormHelperText>
        ) : (
          <FormHelperText style={{ overflowWrap: 'anywhere' }}>
            {t('Security.PasswordFileDisabledHint', {
              path: config.password_path || config.password_file,
            })}
          </FormHelperText>
        )}
        {window.location.protocol !== 'https:' && <FormHelperText error>{t('Security.HttpWarning')}</FormHelperText>}
      </Paper>

      {status.message && (
        <FormHelperText
          error={status.type === 'error'}
          style={{ color: status.type === 'success' ? '#2e7d32' : undefined }}
        >
          {status.message}
        </FormHelperText>
      )}

      <Button
        onClick={save}
        color='secondary'
        variant='contained'
        disabled={loading || saving}
        style={{ marginTop: '4px', minWidth: '220px', minHeight: '42px' }}
      >
        {saving ? t('Security.Saving') : t('Security.Save')}
      </Button>
      <ClientLabelEditor
        open={labelEditor.open}
        address={labelEditor.address}
        label={labelEditor.label}
        allowAddressEdit={labelEditor.allowAddressEdit}
        onClose={() => setLabelEditor(current => ({ ...current, open: false }))}
        onSaved={clientLabelSaved}
      />
    </SecondarySettingsContent>
  )
}
